/**
 * SchemaEditorPage — Tier 4.1 Schema-as-DB Admin UI
 *
 * Allows admins to:
 * - Select an entity and view its base fields
 * - Add custom fields (field_extension)
 * - Override field properties (field_override)
 * - Extend enum values (enum_extension)
 * - Add validation rules (validation_rule)
 * - Modify display settings (display_override)
 * - Preview effective (merged) schema
 */

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Database,
  Plus,
  Trash2,
  Save,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Eye,
  Layers,
  Settings2,
  ListChecks,
  Loader2,
  AlertTriangle,
  CheckCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// ─── Types ────────────────────────────────────────

interface EntityMeta {
  slug: string;
  label: string;
  labelPlural: string;
  icon?: string;
  group?: string;
  database: string;
  category: string;
  fields: FieldMeta[];
  statusColumn?: string;
  statusValues: string[];
  actions: ActionMeta[];
}

interface FieldMeta {
  name: string;
  column: string;
  type: string;
  enumValues?: string[];
  label?: string;
  required?: boolean;
  updatable?: boolean;
  filterable?: boolean;
  searchable?: boolean;
  hidden?: boolean;
}

interface ActionMeta {
  name: string;
  label?: string;
  fromStatus?: string[];
  toStatus?: string;
}

interface SchemaOverride {
  id: string;
  tenant_id: string;
  entity_slug: string;
  override_type: string;
  definition: Record<string, unknown>;
  priority: number;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

interface EffectiveResult {
  slug: string;
  baseMeta: EntityMeta;
  effectiveMeta: EntityMeta;
  overrideCount: number;
  cachedAt: string | null;
}

type OverrideType =
  | 'field_extension'
  | 'field_override'
  | 'enum_extension'
  | 'validation_rule'
  | 'action_override'
  | 'display_override';

// ─── Component ───────────────────────────────────

export default function SchemaEditorPage() {
  const { t } = useTranslation();
  const [entities, setEntities] = useState<EntityMeta[]>([]);
  const [selectedSlug, setSelectedSlug] = useState<string>('');
  const [overrides, setOverrides] = useState<SchemaOverride[]>([]);
  const [effective, setEffective] = useState<EffectiveResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);

  // Form state for adding new override
  const [newType, setNewType] = useState<OverrideType>('field_extension');
  const [newDefinition, setNewDefinition] = useState('{}');
  const [newPriority, setNewPriority] = useState(100);

  // Load entity list
  useEffect(() => {
    api.get<EntityMeta[]>('/entities').then(setEntities).catch(() => {});
  }, []);

  // Load overrides when entity changes
  const loadOverrides = useCallback(async () => {
    if (!selectedSlug) return;
    setLoading(true);
    try {
      const [ov, eff] = await Promise.all([
        api.get<SchemaOverride[]>(`/schema-overrides?entity=${selectedSlug}`),
        api.get<EffectiveResult>(`/schema-overrides/effective/${selectedSlug}`),
      ]);
      setOverrides(ov);
      setEffective(eff);
    } catch {
      setOverrides([]);
      setEffective(null);
    } finally {
      setLoading(false);
    }
  }, [selectedSlug]);

  useEffect(() => {
    loadOverrides();
  }, [loadOverrides]);

  // CRUD handlers
  const handleAdd = async () => {
    if (!selectedSlug) return;
    try {
      const parsed = JSON.parse(newDefinition);
      await api.post('/schema-overrides', {
        entitySlug: selectedSlug,
        overrideType: newType,
        definition: parsed,
        priority: newPriority,
      });
      setShowAddForm(false);
      setNewDefinition('{}');
      loadOverrides();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create override');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this override?')) return;
    try {
      await api.delete(`/schema-overrides/${id}`);
      loadOverrides();
    } catch { /* ignore */ }
  };

  const handleToggle = async (ov: SchemaOverride) => {
    setSavingId(ov.id);
    try {
      await api.patch(`/schema-overrides/${ov.id}`, { enabled: !ov.enabled });
      loadOverrides();
    } finally {
      setSavingId(null);
    }
  };

  const handleInvalidateCache = async () => {
    if (!selectedSlug) return;
    await api.delete(`/schema-overrides/cache/${selectedSlug}`);
    loadOverrides();
  };

  const baseMeta = effective?.baseMeta ?? entities.find((e) => e.slug === selectedSlug);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Database className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-xl font-semibold">Schema Editor</h1>
            <p className="text-sm text-muted-foreground">
              Customize entity schemas per tenant — add fields, override properties, extend enums
            </p>
          </div>
        </div>
        {selectedSlug && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleInvalidateCache}>
              <RefreshCw className="mr-1 h-4 w-4" /> Invalidate Cache
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowPreview(!showPreview)}>
              <Eye className="mr-1 h-4 w-4" /> {showPreview ? 'Hide' : 'Show'} Preview
            </Button>
          </div>
        )}
      </div>

      {/* Entity Selector */}
      <Card>
        <CardContent className="pt-4">
          <label className="text-sm font-medium text-muted-foreground">Select Entity</label>
          <select
            className="mt-1 block w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            value={selectedSlug}
            onChange={(e) => setSelectedSlug(e.target.value)}
          >
            <option value="">— Choose an entity —</option>
            {entities
              .filter((e) => e.database === 'tenant')
              .map((e) => (
                <option key={e.slug} value={e.slug}>
                  {e.label} ({e.slug}) — {e.fields.length} fields
                </option>
              ))}
          </select>
        </CardContent>
      </Card>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {selectedSlug && !loading && (
        <>
          {/* Stats bar */}
          <div className="grid grid-cols-4 gap-4">
            <StatsCard label="Base Fields" value={baseMeta?.fields.length ?? 0} icon={<Layers className="h-4 w-4" />} />
            <StatsCard label="Overrides" value={overrides.length} icon={<Settings2 className="h-4 w-4" />} />
            <StatsCard label="Active" value={overrides.filter((o) => o.enabled).length} icon={<CheckCircle className="h-4 w-4 text-green-500" />} />
            <StatsCard label="Effective Fields" value={effective?.effectiveMeta.fields.length ?? baseMeta?.fields.length ?? 0} icon={<ListChecks className="h-4 w-4" />} />
          </div>

          {/* Overrides List */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-base">Schema Overrides</CardTitle>
              <Button size="sm" onClick={() => setShowAddForm(!showAddForm)}>
                <Plus className="mr-1 h-4 w-4" /> Add Override
              </Button>
            </CardHeader>
            <CardContent>
              {/* Add Form */}
              {showAddForm && (
                <div className="mb-4 rounded-md border border-dashed border-primary/40 bg-primary/5 p-4 space-y-3">
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs font-medium">Type</label>
                      <select
                        className="mt-1 block w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                        value={newType}
                        onChange={(e) => setNewType(e.target.value as OverrideType)}
                      >
                        <option value="field_extension">Field Extension</option>
                        <option value="field_override">Field Override</option>
                        <option value="enum_extension">Enum Extension</option>
                        <option value="validation_rule">Validation Rule</option>
                        <option value="action_override">Action Override</option>
                        <option value="display_override">Display Override</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-medium">Priority</label>
                      <Input
                        type="number"
                        className="mt-1"
                        value={newPriority}
                        onChange={(e) => setNewPriority(Number(e.target.value))}
                      />
                    </div>
                    <div className="flex items-end">
                      <Button size="sm" className="w-full" onClick={handleAdd}>
                        <Save className="mr-1 h-4 w-4" /> Create
                      </Button>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium">Definition (JSON)</label>
                    <textarea
                      className="mt-1 block w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono"
                      rows={6}
                      value={newDefinition}
                      onChange={(e) => setNewDefinition(e.target.value)}
                      placeholder={getPlaceholder(newType)}
                    />
                  </div>
                </div>
              )}

              {/* List */}
              {overrides.length === 0 && !showAddForm ? (
                <p className="py-4 text-center text-sm text-muted-foreground">
                  No schema overrides for this entity. Click "Add Override" to customize.
                </p>
              ) : (
                <div className="space-y-2">
                  {overrides.map((ov) => (
                    <div
                      key={ov.id}
                      className={cn(
                        'flex items-center justify-between rounded-md border px-3 py-2',
                        ov.enabled ? 'border-border' : 'border-border/50 opacity-60',
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <Badge variant={ov.enabled ? 'default' : 'secondary'}>
                          {ov.override_type.replace(/_/g, ' ')}
                        </Badge>
                        <span className="text-xs text-muted-foreground">P{ov.priority}</span>
                        <code className="text-xs text-muted-foreground max-w-md truncate">
                          {JSON.stringify(ov.definition).slice(0, 80)}
                        </code>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggle(ov)}
                          disabled={savingId === ov.id}
                        >
                          {savingId === ov.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : ov.enabled ? 'Disable' : 'Enable'}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(ov.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Effective Schema Preview */}
          {showPreview && effective && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Eye className="h-4 w-4" /> Effective Schema Preview
                  {effective.cachedAt && (
                    <span className="text-xs font-normal text-muted-foreground">
                      Cached: {new Date(effective.cachedAt).toLocaleString()}
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-medium mb-2">Base Fields ({effective.baseMeta.fields.length})</h3>
                    <div className="space-y-1 max-h-96 overflow-auto">
                      {effective.baseMeta.fields.map((f) => (
                        <FieldRow key={f.name} field={f} />
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium mb-2">
                      Effective Fields ({effective.effectiveMeta.fields.length})
                      {effective.overrideCount > 0 && (
                        <Badge variant="outline" className="ml-2">+{effective.overrideCount} overrides</Badge>
                      )}
                    </h3>
                    <div className="space-y-1 max-h-96 overflow-auto">
                      {effective.effectiveMeta.fields.map((f) => {
                        const isNew = !effective.baseMeta.fields.find((bf) => bf.name === f.name);
                        const isChanged = !isNew && JSON.stringify(f) !== JSON.stringify(
                          effective.baseMeta.fields.find((bf) => bf.name === f.name),
                        );
                        return (
                          <FieldRow
                            key={f.name}
                            field={f}
                            highlight={isNew ? 'new' : isChanged ? 'changed' : undefined}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Base Fields Reference */}
          {!showPreview && baseMeta && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Base Fields — {baseMeta.label}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1 max-h-96 overflow-auto">
                  {baseMeta.fields.map((f) => (
                    <FieldRow key={f.name} field={f} />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ─── Sub-components ────────────────────────────────

function StatsCard({ label, value, icon }: { label: string; value: number; icon: React.ReactNode }) {
  return (
    <Card>
      <CardContent className="flex items-center justify-between py-3 px-4">
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="text-lg font-semibold">{value}</p>
        </div>
        {icon}
      </CardContent>
    </Card>
  );
}

function FieldRow({ field, highlight }: { field: FieldMeta; highlight?: 'new' | 'changed' }) {
  return (
    <div
      className={cn(
        'flex items-center justify-between rounded px-2 py-1 text-xs',
        highlight === 'new' && 'bg-green-50 dark:bg-green-950/30',
        highlight === 'changed' && 'bg-yellow-50 dark:bg-yellow-950/30',
      )}
    >
      <div className="flex items-center gap-2">
        <span className="font-mono">{field.name}</span>
        <Badge variant="outline" className="text-[10px]">{field.type}</Badge>
        {field.required && <Badge variant="destructive" className="text-[10px]">req</Badge>}
        {highlight === 'new' && <Badge className="text-[10px] bg-green-500">NEW</Badge>}
        {highlight === 'changed' && <Badge className="text-[10px] bg-yellow-500">MOD</Badge>}
      </div>
      <span className="text-muted-foreground">{field.label || field.column}</span>
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────

function getPlaceholder(type: OverrideType): string {
  switch (type) {
    case 'field_extension':
      return '{\n  "name": "customField",\n  "column": "custom_field",\n  "type": "string",\n  "label": "Custom Field",\n  "filterable": true\n}';
    case 'field_override':
      return '{\n  "fieldName": "status",\n  "label": "Order Status",\n  "filterable": true\n}';
    case 'enum_extension':
      return '{\n  "fieldName": "status",\n  "values": ["custom_status_1", "custom_status_2"]\n}';
    case 'validation_rule':
      return '{\n  "if": { "field": "status", "op": "eq", "value": "completed" },\n  "then": { "field": "completedAt", "required": true },\n  "message": "completedAt is required when completed"\n}';
    case 'action_override':
      return '{\n  "name": "custom_action",\n  "label": "Custom Action",\n  "fromStatus": ["pending"],\n  "toStatus": "processing"\n}';
    case 'display_override':
      return '{\n  "label": "Custom Label",\n  "icon": "box",\n  "group": "custom"\n}';
  }
}
